/**
 * Presentation Context Utilities
 * Provides current PowerPoint presentation context for agents
 */

import { getOrCreatedocumentId } from "../../services/documentIdManager";
import { slideId } from "../../utils/idNormalizer";

export interface PresentationContext {
    documentId: string;
    currentSlideId: string | null;
    currentSlideIndex: number | null;
    totalSlides: number;
    presentationTitle: string;
    allSlideIds: string[];
}

/**
 * Get current presentation context
 * Returns information about the current PowerPoint presentation and selected slide
 */
export async function getCurrentPresentationContext(): Promise<PresentationContext | null> {
    try {
        return await PowerPoint.run(async (context) => {
            // Get deck ID
            const documentId = await getOrCreatedocumentId();
            
            // Get presentation info
            const presentation = context.presentation;
            presentation.load('title');
            
            const slides = presentation.slides;
            slides.load('items');
            
            await context.sync();
            
            // Get filename from document URL (full path like "C:\Users\...\file.pptx")
            let presentationTitle = 'Untitled';
            const documentUrl = Office.context.document.url;
            if (documentUrl) {
                // Extract filename from path (works for both Windows \ and Unix /)
                const pathParts = documentUrl.split(/[/\\]/);
                const fullFilename = pathParts[pathParts.length - 1];
                // Remove .pptx extension if present
                presentationTitle = fullFilename.replace(/\.pptx?$/i, '') || 'Untitled';
            } else {
                // File not saved yet - try to get a better name
                // presentation.title is the document metadata "Title" property, not the filename
                // It often contains unhelpful values like "Title property in win32"
                const title = presentation.title;
                if (title && !title.toLowerCase().includes('property') && title !== 'Untitled') {
                    presentationTitle = title;
                } else {
                    presentationTitle = 'Untitled Presentation';
                }
            }
            const totalSlides = slides.items.length;
            
            // Load all slide IDs
            slides.items.forEach(slide => {
                slide.load('id');
            });
            await context.sync();
            
            const allSlideIds = slides.items.map(slide => slideId(slide.id));
            
            // Get currently selected slide
            let currentSlideId: string | null = null;
            let currentSlideIndex: number | null = null;
            
            try {
                const selectedSlides = presentation.getSelectedSlides();
                selectedSlides.load('items');
                await context.sync();
                
                if (selectedSlides.items.length > 0) {
                    const currentSlide = selectedSlides.items[0];
                    currentSlide.load('id');
                    await context.sync();
                    
                    currentSlideId = slideId(currentSlide.id);
                    
                    // Find the index (1-based) of this slide
                    const slideIndex = allSlideIds.indexOf(currentSlideId);
                    if (slideIndex !== -1) {
                        currentSlideIndex = slideIndex + 1; // Convert to 1-based index
                    }
                }
            } catch (error) {
                console.warn('[PresentationContext] Could not get selected slide:', error);
            }
            
            return {
                documentId,
                currentSlideId,
                currentSlideIndex,
                totalSlides,
                presentationTitle,
                allSlideIds
            };
        });
    } catch (error) {
        console.error('[PresentationContext] Error getting presentation context:', error);
        return null;
    }
}
/**
 * Format presentation context as a string for LLM consumption
 */
export function formatPresentationContextForLLM(context: PresentationContext | null): string {
    if (!context) {
        return "No PowerPoint presentation is currently open or accessible.";
    }
    
    let contextStr = `Current PowerPoint Presentation:
- Title: "${context.presentationTitle}"
- Total Slides: ${context.totalSlides}
- Deck ID: ${context.documentId}`;
    
    if (context.currentSlideId && context.currentSlideIndex) {
        contextStr += `
- Current Selected Slide: Slide ${context.currentSlideIndex} (ID: ${context.currentSlideId})`;
    } else {
        contextStr += `
- Current Selected Slide: None (no slide is currently selected)`;
    }
    
    if (context.allSlideIds.length > 0) {
        contextStr += `
- All Slide IDs: [${context.allSlideIds.slice(0, 5).join(', ')}${context.allSlideIds.length > 5 ? ', ...' : ''}]`;
    }
    
    return contextStr;
}


